// Admin auth flow (no backend).
//
// admin-auth.json lives either in R2 root (public-readable) OR in localStorage (demo mode).
// Format:
// {
//   salt, iv, ciphertext, v:1
//   // payload JSON = { r2?: { accountId,accessKeyId,secretAccessKey,bucket,publicUrl,endpoint? }, mode: "r2"|"local", updated_at }
// }
//
// If mode === "local", no R2 client is available; content is stored in localStorage.

import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { encryptJSON, decryptJSON } from "./crypto";
import {
  fetchPublicJSON,
  makeR2Client,
  uploadJSON,
  getPublicUrl,
  localBackend,
} from "./storage";

const AUTH_KEY = "admin-auth.json";
const LOCAL_AUTH_KEY = "admin-auth-local";
const SESSION_KEY = "tahsin_admin_session_v1";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [state, setState] = useState({
    status: "loading",
    r2: null,
    r2Creds: null,
    authBlob: null,
    mode: null, // "r2" | "local"
  });
  const [loading, setLoading] = useState(true);

  const checkState = useCallback(async () => {
    // Restore session
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) {
        const payload = JSON.parse(raw);
        if (payload?.mode === "r2" && payload.r2?.accessKeyId) {
          setState({
            status: "authenticated",
            r2: makeR2Client(payload.r2),
            r2Creds: payload.r2,
            authBlob: payload.authBlob,
            mode: "r2",
          });
          setLoading(false);
          return;
        }
        if (payload?.mode === "local") {
          setState({
            status: "authenticated",
            r2: null,
            r2Creds: null,
            authBlob: payload.authBlob,
            mode: "local",
          });
          setLoading(false);
          return;
        }
      }
    } catch {
      /* ignore */
    }

    // Check R2 first
    const publicUrl = getPublicUrl();
    if (publicUrl) {
      const blob = await fetchPublicJSON(AUTH_KEY);
      if (blob && blob.ciphertext) {
        setState({ status: "login", r2: null, r2Creds: null, authBlob: blob, mode: "r2" });
        setLoading(false);
        return;
      }
    }

    // Check localStorage admin
    const localBlob = localBackend.loadJSON(LOCAL_AUTH_KEY, null);
    if (localBlob && localBlob.ciphertext) {
      setState({
        status: "login",
        r2: null,
        r2Creds: null,
        authBlob: localBlob,
        mode: "local",
      });
      setLoading(false);
      return;
    }

    setState({ status: "setup", r2: null, r2Creds: null, authBlob: null, mode: null });
    setLoading(false);
  }, []);

  useEffect(() => {
    checkState();
  }, [checkState]);

  const login = async (password) => {
    if (!state.authBlob) throw new Error("فایل احراز هویت یافت نشد.");
    let payload;
    try {
      payload = await decryptJSON(state.authBlob, password);
    } catch {
      throw new Error("رمز عبور نادرست است.");
    }
    const mode = payload.mode || (payload.r2 ? "r2" : "local");
    if (mode === "r2") {
      const r2 = makeR2Client(payload.r2);
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ mode: "r2", r2: payload.r2, authBlob: state.authBlob })
      );
      setState({
        status: "authenticated",
        r2,
        r2Creds: payload.r2,
        authBlob: state.authBlob,
        mode: "r2",
      });
    } else {
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ mode: "local", authBlob: state.authBlob })
      );
      setState({
        status: "authenticated",
        r2: null,
        r2Creds: null,
        authBlob: state.authBlob,
        mode: "local",
      });
    }
    return true;
  };

  const setup = async (fields) => {
    const {
      accountId,
      accessKeyId,
      secretAccessKey,
      bucket,
      publicUrl,
      endpoint,
      password,
      mode,
    } = fields;

    const resolvedMode =
      mode || (accessKeyId && secretAccessKey && bucket && publicUrl ? "r2" : "local");

    if (resolvedMode === "r2") {
      const creds = { accountId, accessKeyId, secretAccessKey, bucket, publicUrl, endpoint };
      localStorage.setItem("tahsin_public_url", publicUrl);
      const r2 = makeR2Client(creds);
      const payload = { mode: "r2", r2: creds, updated_at: new Date().toISOString() };
      const authBlob = await encryptJSON(payload, password);
      await uploadJSON(r2, AUTH_KEY, authBlob);
      sessionStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ mode: "r2", r2: creds, authBlob })
      );
      setState({
        status: "authenticated",
        r2,
        r2Creds: creds,
        authBlob,
        mode: "r2",
      });
    } else {
      const payload = { mode: "local", updated_at: new Date().toISOString() };
      const authBlob = await encryptJSON(payload, password);
      localBackend.saveJSON(LOCAL_AUTH_KEY, authBlob);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify({ mode: "local", authBlob }));
      setState({
        status: "authenticated",
        r2: null,
        r2Creds: null,
        authBlob,
        mode: "local",
      });
    }
  };

  const changePassword = async (oldPassword, newPassword) => {
    if (!state.authBlob) throw new Error("وضعیت احراز هویت معتبر نیست.");
    let payload;
    try {
      payload = await decryptJSON(state.authBlob, oldPassword);
    } catch {
      throw new Error("رمز عبور فعلی نادرست است.");
    }
    const fresh = { ...payload, updated_at: new Date().toISOString() };
    const newBlob = await encryptJSON(fresh, newPassword);
    if (state.mode === "r2") {
      await uploadJSON(state.r2, AUTH_KEY, newBlob);
    } else {
      localBackend.saveJSON(LOCAL_AUTH_KEY, newBlob);
    }
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({
        mode: state.mode,
        r2: state.r2Creds || undefined,
        authBlob: newBlob,
      })
    );
    setState((s) => ({ ...s, authBlob: newBlob }));
    return true;
  };

  const updateR2Config = async (newPartial, currentPassword) => {
    if (state.mode !== "r2")
      throw new Error("این عملیات فقط در حالت R2 قابل انجام است.");
    try {
      await decryptJSON(state.authBlob, currentPassword);
    } catch {
      throw new Error("رمز عبور نادرست است.");
    }
    const creds = { ...state.r2Creds, ...newPartial };
    if (creds.publicUrl) localStorage.setItem("tahsin_public_url", creds.publicUrl);
    const r2 = makeR2Client(creds);
    const payload = { mode: "r2", r2: creds, updated_at: new Date().toISOString() };
    const newBlob = await encryptJSON(payload, currentPassword);
    await uploadJSON(r2, AUTH_KEY, newBlob);
    sessionStorage.setItem(
      SESSION_KEY,
      JSON.stringify({ mode: "r2", r2: creds, authBlob: newBlob })
    );
    setState({
      status: "authenticated",
      r2,
      r2Creds: creds,
      authBlob: newBlob,
      mode: "r2",
    });
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setState((s) => ({
      ...s,
      status: s.authBlob ? "login" : "setup",
      r2: null,
      r2Creds: null,
    }));
  };

  return (
    <AuthContext.Provider
      value={{
        status: state.status,
        loading,
        r2: state.r2,
        r2Creds: state.r2Creds,
        mode: state.mode,
        login,
        setup,
        logout,
        changePassword,
        updateR2Config,
        refresh: checkState,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
